"use client";

import { useState } from "react";
import StorySearch from "./StorySearch";
import { formatUrlForDisplay } from "@/utils/urlUtils";
import type { StoryblokRedirect, StoryblokRedirectInput } from "@/types/redirects";

const RedirectsCard = ({
  redirect,
  updateRedirect,
  deleteRedirect,
  undoChanges,
  isModified,
  isDeleted,
  disabledButtons,
  backgroundColor,
  saveRedirect,
  validationError,
  isLoading,
}: {
  redirect: StoryblokRedirect | StoryblokRedirectInput;
  saveRedirect?: (
    _uid: string,
    source: string,
    destination: string,
    statusCode: string
  ) => void;
  updateRedirect?: (
    _uid: string,
    source: string,
    destination: string,
    statusCode: string,
    uuid?: string
  ) => void;
  deleteRedirect?: (_uid: string) => void;
  undoChanges?: (_uid: string) => void;
  isModified?: boolean;
  isDeleted?: boolean;
  disabledButtons?: boolean;
  backgroundColor?: string;
  validationError?: string;
  isLoading?: boolean;
}) => {
  const [searchBarState, setSearchBarState] = useState([false, false]);

  const controlSearchBar = (from: boolean, to: boolean) => {
    setSearchBarState([from, to]);
  };

  const cardColor = backgroundColor
    ? backgroundColor
    : isDeleted
      ? "bg-red-100"
      : isModified
        ? "bg-orange-100"
        : "bg-gray-50";

  const hasValidationError = validationError && validationError.length > 0;

  return (
    <div
      key={redirect._uid || ''}
      className={`border border-gray-300 p-4 rounded-lg flex justify-between gap-6 w-full mt-3`}
      style={{ backgroundColor: cardColor }}
    >
      <div className="flex flex-col gap-2 w-full">
        <div className="text-xs text-gray-500">{redirect._uid || ''}</div>

        {hasValidationError && (
          <div className="text-red-600 text-sm bg-red-50 p-2 rounded">
            {validationError}
          </div>
        )}

        <div className="flex items-center relative">
          <strong className="w-[50px]">From:</strong>
          <input
            className={`ml-2 p-1 border rounded w-full ${
              hasValidationError && validationError.includes("source")
                ? "border-red-500 bg-red-50"
                : "border-gray-300"
            }`}
            placeholder="/en/example-page"
            value={redirect.oldPath || ""}
            onChange={(e) =>
              updateRedirect?.(
                redirect._uid || '' || '',
                e.target.value,
                redirect.targetPage.cached_url,
                redirect.statusCode
              )
            }
            onFocus={() => controlSearchBar(true, false)}
            disabled={isLoading}
          />
          {searchBarState[0] && (
            <StorySearch
              hideSearchBar={() => controlSearchBar(false, false)}
              updateRedirect={(val: string, uuid: string) =>
                updateRedirect?.(
                  redirect._uid || '',
                  formatUrlForDisplay(val),
                  redirect.targetPage.cached_url,
                  redirect.statusCode,
                  uuid
                )
              }
            />
          )}
        </div>

        <div className="flex items-center relative">
          <strong className="w-[50px]">To:</strong>
          <input
            className={`ml-2 p-1 border rounded w-full ${
              hasValidationError && validationError.includes("destination")
                ? "border-red-500 bg-red-50"
                : "border-gray-300"
            }`}
            placeholder="/en/destination-page"
            value={redirect.targetPage.cached_url || ""}
            onChange={(e) =>
              updateRedirect?.(
                redirect._uid || '',
                redirect.oldPath,
                e.target.value,
                redirect.statusCode
              )
            }
            onFocus={() => controlSearchBar(false, true)}
            disabled={isLoading}
          />
          {searchBarState[1] && (
            <StorySearch
              hideSearchBar={() => controlSearchBar(false, false)}
              updateRedirect={(val: string, uuid: string) =>
                updateRedirect?.(
                  redirect._uid || '',
                  redirect.oldPath,
                  formatUrlForDisplay(val),
                  redirect.statusCode,
                  uuid
                )
              }
            />
          )}
        </div>

        <div className="flex items-center">
          <strong className="w-[50px]">Code:</strong>
          <select
            className="ml-2 p-1 border border-gray-300 rounded w-full"
            value={redirect.statusCode}
            onChange={(e) =>
              updateRedirect?.(
                redirect._uid || '',
                redirect.oldPath,
                redirect.targetPage.cached_url,
                e.target.value
              )
            }
            disabled={isLoading}
          >
            <option value="301">301 - Permanent redirect</option>
            <option value="302">302 - Temporary redirect</option>
            <option value="307">307 - Temporary redirect (preserve method)</option>
            <option value="308">308 - Permanent redirect (preserve method)</option>
          </select>
        </div>
      </div>

      {!disabledButtons && (
        <div className="flex flex-col justify-between w-20">
          {!isDeleted ? (
            <button
              className="mt-2 px-2 py-1 bg-red-500 text-white rounded hover:bg-red-600 disabled:opacity-50 disabled:cursor-not-allowed"
              onClick={() => deleteRedirect?.(redirect._uid || '')}
              disabled={isLoading}
            >
              {isLoading ? "..." : "Delete"}
            </button>
          ) : (
            <div></div>
          )}

          {isModified && (
            <>
              <button
                className="mt-2 px-2 py-1 bg-gray-300 text-black rounded hover:bg-gray-400 disabled:opacity-50 disabled:cursor-not-allowed"
                onClick={() =>
                  saveRedirect?.(
                    redirect._uid || '',
                    redirect.oldPath,
                    redirect.targetPage.cached_url,
                    redirect.statusCode
                  )
                }
                disabled={isLoading}
              >
                {isLoading ? "..." : "Save"}
              </button>
              <button
                className="mt-2 px-2 py-1 bg-green-300 text-black rounded hover:bg-green-400 disabled:opacity-50 disabled:cursor-not-allowed"
                onClick={() => undoChanges?.(redirect._uid || '')}
                disabled={isLoading}
              >
                Undo
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default RedirectsCard;
